import React, { useEffect, useState } from 'react'
import CardFilters from '../components/CardFilters'
import Search from '../components/Search'
import SortControls from '../components/SortControls'
import ContentGrid from '../components/ContentGrid'
import { normalizeString } from '../utils/helpers'
import { safeReadJson, safeWriteJson } from '../utils/storage'
import { getCards, onCardsUpdated } from '../utils/contentRepository'

const COST_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8']
const POWER_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
const SERIES_OPTIONS = [
  'Series 1',
  'Series 2',
  'Series 3',
  'Series 4',
  'Series 5',
  'Not Available',
  'Season Pass',
  'Available',
  'None',
  'Starter Card',
  'Collection Level 1-14',
  'Recruit Season',
]
const TYPE_OPTIONS = [
  'Activate',
  'Destroy',
  'Discard',
  'End of Turn',
  'Game Start',
  'Start of Game',
  'Move',
  'No Ability',
  'On Reveal',
  'Ongoing',
  'Skill',
]

const sanitizeStoredArray = (key, allowed) => {
  const value = safeReadJson(key, [])
  if (!Array.isArray(value)) return []

  const normalized = value.map((item) => String(item).trim())
  return normalized.filter((item, index) => allowed.includes(item) && normalized.indexOf(item) === index)
}

const CardsView = ({ language }) => {
  const [cards, setCards] = useState(() => getCards())
  const [searchTerm, setSearchTerm] = useState('')
  const [costFilter, setCostFilter] = useState(() =>
    sanitizeStoredArray('costFilter_cards', COST_OPTIONS)
  )
  const [powerFilter, setPowerFilter] = useState(() =>
    sanitizeStoredArray('powerFilter_cards', POWER_OPTIONS)
  )
  const [selectedSeries, setSelectedSeries] = useState(() =>
    sanitizeStoredArray('selectedSeries_cards', SERIES_OPTIONS)
  )
  const [selectedTypes, setSelectedTypes] = useState(() =>
    sanitizeStoredArray('selectedTypes_cards', TYPE_OPTIONS)
  )
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [filtersOpen, setFiltersOpen] = useState(() =>
    safeReadJson('filtersOpen_cards', false)
  )

  useEffect(() => onCardsUpdated(setCards), [])

  useEffect(() => {
    safeWriteJson('costFilter_cards', costFilter)
  }, [costFilter])

  useEffect(() => {
    safeWriteJson('powerFilter_cards', powerFilter)
  }, [powerFilter])

  useEffect(() => {
    safeWriteJson('selectedSeries_cards', selectedSeries)
  }, [selectedSeries])

  useEffect(() => {
    safeWriteJson('selectedTypes_cards', selectedTypes)
  }, [selectedTypes])

  useEffect(() => {
    safeWriteJson('filtersOpen_cards', filtersOpen)
  }, [filtersOpen])

  const resetFilters = () => {
    setCostFilter([])
    setPowerFilter([])
    setSelectedSeries([])
    setSelectedTypes([])
    setSortBy('name')
    setSortOrder('asc')
    setSearchTerm('')
  }

  const textFilter = (item) => {
    const normalizedSearch = normalizeString(searchTerm)
    const fieldsToSearch = [
      item.name,
      item.descr,
      item.nameEN,
      item.descrEN,
    ].map((field) => normalizeString(field))

    return fieldsToSearch.some((field) => field.includes(normalizedSearch))
  }

  const filteredCards = cards
    .filter((item) => {
      const availableCategories = [
        'Series 1',
        'Series 2',
        'Series 3',
        'Series 4',
        'Series 5',
        'Starter Card',
        'Collection Level 1-14',
        'Recruit Season',
        'Season Pass',
        'Not Available',
      ]

      const cost = String(item.cost)
      const power = Number(item.power)

      const matchesText = searchTerm ? textFilter(item) : true
      const matchesCost = costFilter.length ? costFilter.includes(cost) : true
      const matchesPower = powerFilter.length
        ? powerFilter.some((val) => {
            const valNum = Number(val)
            return val === '10' ? power >= 10 : power === valNum
          })
        : true

      const matchesAvailable = selectedSeries.includes('Available')
        ? availableCategories.includes(item.category)
        : false
      const matchesNone = selectedSeries.includes('None')
        ? item.category === 'None'
        : false
      const matchesSeries =
        selectedSeries.includes('Available') || selectedSeries.includes('None')
          ? matchesAvailable || matchesNone
          : selectedSeries.length
            ? selectedSeries.includes(item.category)
            : true

      const itemTypes = Array.isArray(item.type) ? item.type : JSON.parse(item.type)
      const matchesType = selectedTypes.length
        ? itemTypes.some((type) => selectedTypes.includes(type))
        : true

      return matchesText && matchesCost && matchesPower && matchesSeries && matchesType
    })
    .sort((a, b) => {
      const modifier = sortOrder === 'asc' ? 1 : -1
      const compareField =
        sortBy === 'nameEn'
          ? 'nameEN'
          : sortBy === 'name'
            ? language === 'EN'
              ? 'nameEN'
              : 'name'
            : sortBy

      switch (sortBy) {
        case 'name':
        case 'nameEn':
          return a[compareField].localeCompare(b[compareField], language) * modifier
        case 'cost':
          return (Number(a.cost) - Number(b.cost)) * modifier
        case 'power':
          return (Number(a.power) - Number(b.power)) * modifier
        default:
          return 0
      }
    })

  return (
    <>
      <CardFilters
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        costFilter={costFilter}
        setCostFilter={setCostFilter}
        powerFilter={powerFilter}
        setPowerFilter={setPowerFilter}
        selectedSeries={selectedSeries}
        setSelectedSeries={setSelectedSeries}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
        language={language}
      />

      <Search
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        language={language}
      />

      <SortControls
        activeView="cards"
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        resetFilters={resetFilters}
        filteredCount={filteredCards.length}
        totalCount={cards.length}
        language={language}
      />

      <ContentGrid activeView="cards" data={filteredCards} language={language} />
    </>
  )
}

export default CardsView
